import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Button, Separator, FlexView } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import { Grid } from 'semantic-ui-react';

import { renderFormField, HeaderHead, Message } from 'modules/form';
import {
  TRIAL_PROFILE_FIELDS,
  TRAINING_COMPLETED_VERSION,
  TEACHER_STATE_CHANGE_REASON,
} from 'constants/form';
import {
  useGetTrialProfile,
  usePostTrialProfile,
  invalidateTrialProfile,
} from 'actions/trial-profile';

const HeaderHeadWrapper = styled(HeaderHead)`
  padding: 10px;
`;

const getFormDefaults = (data = {}, fields) => {
  const formDefaults = {};

  fields?.forEach(field => {
    formDefaults[field.name] =
      !!data && (typeof data[field.param] === 'number' || data[field.param])
        ? data[field.param]
        : field.default;
  });

  return formDefaults;
};

const TrialProfile = ({ teacherId }) => {
  const [updatedFormData, setUpdatedFormData] = useState([]);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [formFields, setFormFields] = useState(TRIAL_PROFILE_FIELDS);

  const { get, data, isStale } = useGetTrialProfile(teacherId);
  const { state: teacherState = 'NA', state_reason: stateReason = 'NA' } = data || {};
  const { post, isProcessing } = usePostTrialProfile({
    onComplete: error => {
      if (error) {
        setInfoText({ error });
      } else {
        invalidateTrialProfile(teacherId);

        setUpdatedFormData([]);
        setFormFields(TRIAL_PROFILE_FIELDS);
        setInfoText({ success: 'Updated Successfully!' });
      }

      setIsButtonDisabled(true);
    },
  });

  const [formData, setFormData] = useState({});
  const [infoText, setInfoText] = useState(null);
  const { error, success } = infoText || {};

  const { state: teacherStateFormData } = formData || {};
  const {
    state: teacherStateUpdatedFormData,
    state_reason: teacherStateReasonUpdatedFormData,
    training_completed_version: trainingCompletedVersionUpdatedFormData,
  } = updatedFormData || {};

  const appendFormFields = useCallback(fields => {
    setFormFields([...formFields, ...fields]);
  }, []);

  const onChange = (key, value, type) => {
    const stateChanges = {
      ...formData,
      [key]: value,
    };
    if (key === 'state') {
      stateChanges['state_reason'] = '';
      if (value === 'TRAINING_COMPLETE') {
        stateChanges['training_completed_version'] = '';
      }
    }
    setFormData(stateChanges);
    setUpdatedFormData({ ...updatedFormData, [key]: type === 'number' ? +value : value });
    setIsButtonDisabled(false);
  };

  const onSubmit = useCallback(() => {
    if (teacherStateUpdatedFormData) {
      if (teacherStateUpdatedFormData === 'TRAINING_COMPLETE') {
        if (
          !trainingCompletedVersionUpdatedFormData &&
          !teacherStateReasonUpdatedFormData
        ) {
          setInfoText({
            error:
              'Please fill the training completed version and reason for state change',
          });

          return;
        }

        if (!trainingCompletedVersionUpdatedFormData) {
          setInfoText({ error: 'Please fill the training completed version' });

          return;
        }
      }

      if (!teacherStateReasonUpdatedFormData) {
        setInfoText({ error: 'Please fill the reason for state change' });

        return;
      }
    }

    post(updatedFormData, teacherId);
  }, [
    post,
    teacherId,
    teacherStateReasonUpdatedFormData,
    teacherStateUpdatedFormData,
    trainingCompletedVersionUpdatedFormData,
    updatedFormData,
  ]);

  useEffect(() => {
    if (data) {
      const formDefaults = getFormDefaults(data || {}, formFields);
      setFormData(formDefaults);
    }
  }, [data]);

  useEffect(() => {
    if (!data || isStale) {
      get(teacherId);
    }
  }, [data, get, isStale, teacherId]);

  useEffect(() => {
    if (teacherStateUpdatedFormData === 'TRAINING_COMPLETE') {
      appendFormFields([TRAINING_COMPLETED_VERSION, TEACHER_STATE_CHANGE_REASON]);
    } else if (teacherStateUpdatedFormData) {
      appendFormFields([TEACHER_STATE_CHANGE_REASON]);
    }
  }, [appendFormFields, teacherState, teacherStateFormData, teacherStateUpdatedFormData]);

  const renderFormFields = () => {
    return (
      <FlexView>
        <Grid>
          {formFields?.map(field => {
            const { disabled, editable, name, fieldType, type } = field || {};
            const fieldDisabled = disabled || !editable;
            const placeholder = fieldDisabled ? '' : field.label;

            return (
              <Grid.Column mobile={16} tablet={16} computer={8}>
                {renderFormField({
                  ...field,
                  profileView: false,
                  value: formData[name],
                  onChange:
                    fieldType === 'SELECT'
                      ? (key, value) => onChange(key, value, type)
                      : value => onChange(name, value, type),
                  disabled: fieldDisabled,
                  placeholder,
                })}
              </Grid.Column>
            );
          })}
        </Grid>
      </FlexView>
    );
  };

  return (
    <FlexView>
      <HeaderHeadWrapper>
        <Text variant="body1Medium">Trial Info</Text>
      </HeaderHeadWrapper>
      <Separator height={20} transparent />
      <FlexView flexDirection="row" alignItems="center">
        <Text variant="body1Medium">Current State: </Text>
        <Text variant="body2" marginHorizontal="8px 8px">
          {teacherState?.replace(/_/g, ' ') || 'NA'}
        </Text>
      </FlexView>
      <FlexView flexDirection="row" alignItems="center">
        <Text variant="body1Medium">Current State Reason: </Text>
        <Text variant="body2" marginHorizontal="8px 8px">
          {stateReason || 'NA'}
        </Text>
      </FlexView>
      <Separator height={20} transparent />
      <Separator height={1} />
      <Separator height={20} transparent />
      {renderFormFields()}
      <Separator height={20} transparent />
      <FlexView alignItems="center">
        <Button
          align="center"
          label="Submit"
          onClick={onSubmit}
          disabled={isButtonDisabled}
          busy={isProcessing}
        />
      </FlexView>
      <Separator height={10} transparent />
      {error && (
        <FlexView alignItems="center">
          <Message error={true}>{error}</Message>
        </FlexView>
      )}
      {success && (
        <FlexView alignItems="center">
          <Message>{success}</Message>
        </FlexView>
      )}
    </FlexView>
  );
};

export default TrialProfile;
